/**
 * @fileoverview Universal Email Section Component
 * 
 * Displays and manages emails for any entity type that supports email communication.
 * Adapts behavior based on entity config (direct association, participant filter, etc.)
 * 
 * Features:
 * - Multiple email scopes (entity, participant, related)
 * - Communication mode switching (optional)
 * - Email list and detail view
 * - Send new emails
 * - Filter emails based on scope
 * 
 * @module Components/UniversalDetailPage/UniversalEmailSection
 */

import React, { useState } from 'react';
import { Switch } from "@heroui/react";
import { UniversalEntityConfig } from "shared/types/universalEntityConfig";
import { EmailsSection } from "screens/helpdesk/helpdeskTicketDetail/components/EmailsSection";

interface UniversalEmailSectionProps {
  /** Current entity */
  entity: any;
  
  /** Entity configuration */
  entityConfig: UniversalEntityConfig;
  
  /** Callback when entity is updated */
  onEntityUpdate: (updated: any) => void;
}

export const UniversalEmailSection: React.FC<UniversalEmailSectionProps> = ({
  entity,
  entityConfig,
}) => {
  const emailConfig = entityConfig.emails;

  // Early return if emails not enabled
  if (!emailConfig?.enabled) {
    return null;
  }

  // State for communication mode (if applicable)
  // At this point, TypeScript knows emailConfig is EntityEmailConfig
  const [communicationMode, setCommunicationMode] = useState<string>(
    emailConfig.communicationModes?.[0]?.id || 'default'
  );
  
  // For helpdesk tickets, reuse existing EmailsSection component
  // This provides backward compatibility
  if (entityConfig.entityName === 'ticket') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
        <EmailsSection 
          ticket={entity}
          communicationMode={communicationMode as 'tenant' | 'supplier'}
          isInsurance={false}
          onNewEmail={() => {
            // This will be handled by the component itself
          }}
          onRefreshRequest={() => {
            // Refresh callback if needed
          }}
        />
        
        {/* Communication Mode Toggle (if applicable) */}
        {emailConfig.communicationModes && emailConfig.communicationModes.length > 1 && (
          <div className="absolute top-4 right-4">
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg border border-neutral-primary/20 bg-white/80">
              {emailConfig.communicationModes.map((mode, idx) => (
                <React.Fragment key={mode.id}>
                  {idx > 0 && (
                    <Switch
                      size="sm"
                      isSelected={communicationMode === emailConfig.communicationModes![idx].id}
                      onChange={() => setCommunicationMode(
                        communicationMode === emailConfig.communicationModes![0].id 
                          ? emailConfig.communicationModes![1].id 
                          : emailConfig.communicationModes![0].id
                      )}
                      classNames={{
                        wrapper: "bg-neutral-200",
                        thumb: "bg-white"
                      }}
                    />
                  )}
                  <span className={`text-sm ${communicationMode === mode.id ? 'font-medium text-moss-primary' : 'text-neutral-secondary-mid'}`}>
                    {mode.label}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // For other entities, we'd implement a generic email view here
  // For now, return placeholder
  return (
    <div className="bg-muted rounded-lg border border-muted-foreground/20 shadow-sm p-6">
      <div className="text-center text-neutral-secondary-mid">
        <p className="mb-4">Email functionality for {entityConfig.displayName}</p>
        <p className="text-sm">Coming soon - Generic email viewer for all entity types</p>
      </div>
    </div>
  );
};

